// Largely derived from the following:
// https://github.com/iamhosseindhv/notistack/blob/master/examples/redux-example/redux/actions.js

import { getAppMonitor } from '../monitor';
import { enqueueSnackbar, Notification, NotificationLinkButton, NotificationType } from '../store/notificationsSlice';

function createNotification(
  variant: NotificationType,
  message: string,
  options: Pick<Notification, 'linkButton' | 'permanent' | 'waitForDismissal'>,
): Notification {
  const timestamp = Date.now();
  return { key: timestamp + Math.random(), addedAt: timestamp, options: { variant }, message, ...options };
}

export function createErrorNotification(message: string, waitForDismissal: boolean): Notification {
  return createNotification('error', message, { waitForDismissal });
}

function createPermanentErrorNotification(message: string): Notification {
  return createNotification('error', message, { permanent: true });
}

export function createWarningNotification(message: string, waitForDismissal: boolean): Notification {
  return createNotification('warning', message, { waitForDismissal });
}

function createPermanentWarningNotification(message: string): Notification {
  return createNotification('warning', message, { permanent: true });
}

function createSuccessNotification(message: string, waitForDismissal: boolean): Notification {
  return createNotification('success', message, { waitForDismissal });
}

function createInfoNotification(
  message: string,
  waitForDismissal: boolean,
  action: NotificationLinkButton | undefined,
): Notification {
  return createNotification('info', message, { waitForDismissal, linkButton: action });
}

function reportNotificationToMonitor(type: string, message: string, metadata: Record<string, unknown>) {
  // using `setTimeout` to ensure monitoring can't block or break the notification
  setTimeout(() => {
    getAppMonitor()?.recordInteraction('Notification Shown', { type, message, ...metadata });
  });
}

export const enqueueError = (error: string, waitForDismissal = true) => {
  reportNotificationToMonitor('error', error, { waitForDismissal });
  return enqueueSnackbar(createErrorNotification(error, waitForDismissal));
};

export const enqueuePermanentError = (error: string) => {
  reportNotificationToMonitor('error', error, { permanent: true });
  return enqueueSnackbar(createPermanentErrorNotification(error));
};

export const enqueueWarning = (warning: string, waitForDismissal = true) => {
  reportNotificationToMonitor('warning', warning, { waitForDismissal });
  return enqueueSnackbar(createWarningNotification(warning, waitForDismissal));
};

export const enqueuePermanentWarning = (warning: string) => {
  reportNotificationToMonitor('warning', warning, { permanent: true });
  return enqueueSnackbar(createPermanentWarningNotification(warning));
};

export const enqueueSuccess = (success: string, waitForDismissal = false) => {
  reportNotificationToMonitor('success', success, { waitForDismissal });
  return enqueueSnackbar(createSuccessNotification(success, waitForDismissal));
};

export const enqueueInfo = (info: string, waitForDismissal = false, action?: NotificationLinkButton) => {
  reportNotificationToMonitor('info', info, { waitForDismissal });
  return enqueueSnackbar(createInfoNotification(info, waitForDismissal, action));
};
